<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { Content } from '~/components/ui/content'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetCustomerCare } from '~/components/widget/customer-care'
import { LucideCopy, LucideCheck, LucideZap, LucideKey } from 'lucide-vue-next'
import { toast } from '~/components/ui/toast'
import { FeatureContactPageForm } from '~/components/feature/contact-page/form'

const breadcrumbs = [{ title: 'Partner API', url: '/partner' }]

// Code examples
const authHeaderCode = `Authorization: Bearer YOUR_ACCESS_TOKEN`

const getLeadsRequest = `curl -X GET "https://staging.banks.kg/api/partner/leads?type=credit&page=1" \\
  -H "Authorization: Bearer YOUR_TOKEN"`

const patchLeadsRequest = `curl -X PATCH "https://staging.banks.kg/api/partner/leads" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"lead_id": 123, "status": "processed"}'`

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
  toast({ title: 'Скопировано' })
}
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetCustomerCare />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>

        <div class="lg:col-span-3" role="main">
          <!-- Hero Section -->
          <ContentBlock variant="white">
            <Heading tag="h1">Partner API Banks.kg</Heading>
            <Content>
              <div class="mb-8 text-lg text-gray-600">
                <p class="!text-base">
                  API для получения и обработки лидов от Banks.kg. Интегрируйте наш сервис для
                  автоматического получения заявок от клиентов в реальном времени.
                </p>
              </div>

              <!-- Benefits Cards -->
              <div class="mb-10">
                <h3 class="!mb-6 !text-xl">Преимущества Partner API</h3>
                <div class="grid gap-4 md:grid-cols-2">
                  <div
                    class="flex gap-4 rounded-lg border border-blue-100 bg-blue-50/50 p-5 transition-all hover:border-blue-200 hover:shadow-md"
                  >
                    <div class="shrink-0">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600"
                      >
                        <LucideZap :size="20" class="text-white" />
                      </div>
                    </div>
                    <div>
                      <p class="!m-0 font-medium text-gray-900">
                        Получайте лиды в реальном времени
                      </p>
                      <p class="!mb-0 !mt-1 text-sm text-gray-600">
                        Моментальная передача заявок через API без задержек
                      </p>
                    </div>
                  </div>
                  <div
                    class="flex gap-4 rounded-lg border border-green-100 bg-green-50/50 p-5 transition-all hover:border-green-200 hover:shadow-md"
                  >
                    <div class="shrink-0">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-lg bg-green-600"
                      >
                        <LucideCheck :size="20" class="text-white" />
                      </div>
                    </div>
                    <div>
                      <p class="!m-0 font-medium text-gray-900">
                        Автоматическая обработка статусов
                      </p>
                      <p class="!mb-0 !mt-1 text-sm text-gray-600">
                        Обновляйте статус обработки заявок через API
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Authentication Section -->
              <div class="mb-10">
                <div class="mb-4 flex items-center gap-3">
                  <LucideKey :size="24" class="text-blue-600" />
                  <h3 class="!mb-0 !text-xl">Аутентификация</h3>
                </div>
                <p class="!mb-4 text-gray-600">
                  Все запросы к API требуют аутентификации через Bearer токен в заголовке
                  Authorization. Токен предоставляется после подключения к партнерской программе.
                </p>
                <div
                  class="group relative overflow-hidden rounded-xl border border-gray-200 shadow-sm"
                >
                  <div
                    class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2"
                  >
                    <span class="text-xs font-medium text-gray-600">HTTP Header</span>
                    <button
                      class="CopyButton"
                      title="Копировать"
                      @click="copyToClipboard(authHeaderCode)"
                    >
                      <LucideCopy :size="14" />
                      <span>Копировать</span>
                    </button>
                  </div>
                  <pre
                    class="overflow-x-auto bg-gray-50 p-4 text-sm leading-relaxed"
                  ><code><span class="text-purple-600">Authorization:</span> <span class="text-blue-600">Bearer</span> <span class="text-green-600">YOUR_ACCESS_TOKEN</span></code></pre>
                </div>
              </div>

              <!-- GET /leads Endpoint -->
              <div class="mb-10">
                <h3 class="!mb-4 !text-xl">Получение лидов</h3>
                <p class="!mb-6 text-gray-600">
                  Эндпоинт для получения списка лидов с возможностью фильтрации по дате, типу
                  продукта и статусу обработки.
                </p>

                <div class="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-4">
                  <code class="text-sm font-medium text-blue-900"
                    >GET https://staging.banks.kg/api/partner/leads</code
                  >
                </div>

                <!-- Parameters Table -->
                <h4 class="!mb-3 !mt-6 !text-lg">Параметры запроса</h4>
                <div class="mb-6 overflow-hidden rounded-xl border border-gray-200">
                  <table class="w-full">
                    <thead>
                      <tr class="border-b border-gray-200 bg-gray-50">
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Параметр
                        </th>
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">Тип</th>
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Описание
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >date_from</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">string</td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Дата начала периода (формат: YYYY-MM-DD)
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >date_to</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">string</td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Дата окончания периода (формат: YYYY-MM-DD)
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >type</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">string</td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Тип продукта: credit, deposit, card
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >with_processed</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">integer</td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Включить обработанные лиды: 0 - только новые, 1 - все
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >page</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">integer</td>
                        <td class="px-6 py-4 text-sm text-gray-700">Номер страницы</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Request Example -->
                <h4 class="!mb-3 !text-lg">Пример запроса</h4>
                <div
                  class="group relative mb-6 overflow-hidden rounded-xl border border-gray-200 shadow-sm"
                >
                  <div
                    class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2"
                  >
                    <span class="text-xs font-medium text-gray-600">cURL</span>
                    <button
                      class="CopyButton"
                      title="Копировать"
                      @click="copyToClipboard(getLeadsRequest)"
                    >
                      <LucideCopy :size="14" />
                      <span>Копировать</span>
                    </button>
                  </div>
                  <pre
                    class="overflow-x-auto bg-gray-50 p-4 text-sm leading-relaxed"
                  ><code><span class="text-blue-600">curl</span> -X GET <span class="text-green-600">"https://staging.banks.kg/api/partner/leads?type=credit&page=1"</span> \
  -H <span class="text-green-600">"Authorization: Bearer YOUR_TOKEN"</span></code></pre>
                </div>

                <!-- Response Example -->
                <h4 class="!mb-3 !text-lg">Пример ответа</h4>
                <div class="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                  <div class="border-b border-gray-200 bg-gray-50 px-4 py-2">
                    <span class="text-xs font-medium text-gray-600">JSON Response</span>
                  </div>
                  <pre
                    class="overflow-x-auto bg-gray-50 p-4 text-sm leading-relaxed"
                  ><code><span class="text-gray-700">{</span>
  <span class="text-purple-600">"data"</span>: [
    {
      <span class="text-purple-600">"id"</span>: <span class="text-blue-600">123</span>,
      <span class="text-purple-600">"type"</span>: <span class="text-green-600">"credit"</span>,
      <span class="text-purple-600">"product_name"</span>: <span class="text-green-600">"Потребительский кредит"</span>,
      <span class="text-purple-600">"location"</span>: <span class="text-green-600">"Бишкек"</span>,
      <span class="text-purple-606">"email"</span>: <span class="text-green-600">"client@example.com"</span>,
      <span class="text-purple-600">"phone"</span>: <span class="text-green-600">"+996555123456"</span>,
      <span class="text-purple-600">"created_at"</span>: <span class="text-green-600">"2024-01-15 10:30:00"</span>,
      <span class="text-purple-600">"names"</span>: <span class="text-green-600">"Иванов Иван Иванович"</span>,
      <span class="text-purple-600">"comments"</span>: <span class="text-green-600">"Требуется срочный кредит"</span>,
      <span class="text-purple-600">"address"</span>: <span class="text-green-600">"ул. Чуй 123"</span>,
      <span class="text-purple-600">"amount"</span>: <span class="text-blue-600">500000</span>,
      <span class="text-purple-600">"currency"</span>: <span class="text-green-600">"KGS"</span>,
      <span class="text-purple-600">"period"</span>: <span class="text-blue-600">12</span>,
      <span class="text-purple-600">"credit_purpose"</span>: <span class="text-green-600">"Ремонт квартиры"</span>
    }
  ],
  <span class="text-purple-600">"current_page"</span>: <span class="text-blue-600">1</span>,
  <span class="text-purple-600">"last_page"</span>: <span class="text-blue-600">5</span>,
  <span class="text-purple-600">"per_page"</span>: <span class="text-blue-600">20</span>,
  <span class="text-purple-600">"total"</span>: <span class="text-blue-600">100</span>
<span class="text-gray-700">}</span></code></pre>
                </div>
              </div>

              <!-- PATCH /leads Endpoint -->
              <div class="mb-10">
                <h3 class="!mb-4 !text-xl">Обновление статуса лида</h3>
                <p class="!mb-6 text-gray-600">
                  Эндпоинт для обновления статуса обработки лида. Позволяет отметить лид как
                  обработанный или отклоненный.
                </p>

                <div class="mb-4 rounded-lg border border-green-100 bg-green-50 p-4">
                  <code class="text-sm font-medium text-green-900"
                    >PATCH https://staging.banks.kg/api/partner/leads</code
                  >
                </div>

                <!-- Parameters Table -->
                <h4 class="!mb-3 !mt-6 !text-lg">Параметры тела запроса</h4>
                <div class="mb-6 overflow-hidden rounded-xl border border-gray-200">
                  <table class="w-full">
                    <thead>
                      <tr class="border-b border-gray-200 bg-gray-50">
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Параметр
                        </th>
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">Тип</th>
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Описание
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >lead_id</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">integer</td>
                        <td class="px-6 py-4 text-sm text-gray-700">ID лида для обновления</td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-gray-100 px-2 py-1 font-mono text-sm text-blue-600"
                            >status</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">string</td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Новый статус: "processed" (обработан) или "declined" (отклонен)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Request Example -->
                <h4 class="!mb-3 !text-lg">Пример запроса</h4>
                <div
                  class="group relative mb-6 overflow-hidden rounded-xl border border-gray-200 shadow-sm"
                >
                  <div
                    class="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2"
                  >
                    <span class="text-xs font-medium text-gray-600">cURL</span>
                    <button
                      class="CopyButton"
                      title="Копировать"
                      @click="copyToClipboard(patchLeadsRequest)"
                    >
                      <LucideCopy :size="14" />
                      <span>Копировать</span>
                    </button>
                  </div>
                  <pre
                    class="overflow-x-auto bg-gray-50 p-4 text-sm leading-relaxed"
                  ><code><span class="text-blue-600">curl</span> -X PATCH <span class="text-green-600">"https://staging.banks.kg/api/partner/leads"</span> \
  -H <span class="text-green-600">"Authorization: Bearer YOUR_TOKEN"</span> \
  -H <span class="text-green-600">"Content-Type: application/json"</span> \
  -d <span class="text-green-600">'{"lead_id": 123, "status": "processed"}'</span></code></pre>
                </div>

                <!-- Response Example -->
                <h4 class="!mb-3 !text-lg">Пример ответа</h4>
                <div class="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                  <div class="border-b border-gray-200 bg-gray-50 px-4 py-2">
                    <span class="text-xs font-medium text-gray-600">JSON Response</span>
                  </div>
                  <pre
                    class="overflow-x-auto bg-gray-50 p-4 text-sm leading-relaxed"
                  ><code><span class="text-gray-700">{</span>
  <span class="text-purple-600">"message"</span>: <span class="text-green-600">"Статус лида успешно обновлен"</span>
<span class="text-gray-700">}</span></code></pre>
                </div>
              </div>

              <!-- Response Codes Table -->
              <div class="mb-10">
                <h3 class="!mb-6 !text-xl">Коды ответов</h3>
                <div class="overflow-hidden rounded-xl border border-gray-200">
                  <table class="w-full">
                    <thead>
                      <tr class="border-b border-gray-200 bg-gray-50">
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">Код</th>
                        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          Описание
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200 bg-white">
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-green-100 px-2 py-1 font-mono text-sm text-green-700"
                            >200</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">Успешный запрос</td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code class="rounded bg-red-100 px-2 py-1 font-mono text-sm text-red-700"
                            >401</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Неавторизован - неверный или отсутствующий токен
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code class="rounded bg-red-100 px-2 py-1 font-mono text-sm text-red-700"
                            >403/404</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Доступ запрещен / Ресурс не найден
                        </td>
                      </tr>
                      <tr class="transition-colors hover:bg-gray-50">
                        <td class="px-6 py-4">
                          <code
                            class="rounded bg-yellow-100 px-2 py-1 font-mono text-sm text-yellow-700"
                            >422</code
                          >
                        </td>
                        <td class="px-6 py-4 text-sm text-gray-700">
                          Ошибка валидации - некорректные параметры запроса
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </Content>
          </ContentBlock>

          <!-- Contact Form -->
          <ContentBlock variant="white" class="mt-6">
            <FeatureContactPageForm heading-tag="h3" heading-text="Нужен доступ к API?" />
          </ContentBlock>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
